import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, TrendingUp } from 'lucide-react';

const CourseProgressCards = ({ courseEnrollmentAnalysis }) => {
  if (!courseEnrollmentAnalysis || courseEnrollmentAnalysis.totalCourses === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No course enrollment data available</p>
      </motion.div>
    );
  }

  const { totalCourses, activeCourses, completedCourses, courseProgress, averageCourseProgress } = courseEnrollmentAnalysis;

  const getEngagementColor = (level) => {
    switch (level) {
      case 'high':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'low':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-cyan-600 rounded-lg">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Course Progress</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{totalCourses} courses enrolled</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Active</p>
          <p className="text-3xl font-bold text-cyan-600">{activeCourses}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Completed</p>
          <p className="text-3xl font-bold text-green-600">{completedCourses}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 text-center"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Avg. Progress</p>
          <p className="text-3xl font-bold text-blue-600">{averageCourseProgress}%</p>
        </motion.div>
      </div>

      {/* Course Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {courseProgress.slice(0, 4).map((course, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">
                {course.courseName}
              </h4>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEngagementColor(course.engagementLevel)}`}>
                {course.engagementLevel}
              </span>
            </div>

            <div className="mb-2">
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                <span>Progress</span>
                <span className="font-bold">{course.progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${course.progress}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                  className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600"
                />
              </div>
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last accessed: {new Date(course.lastAccessed).toLocaleDateString()}
            </p>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default CourseProgressCards;
